/**
 * OAuth / email-link callback plumbing (spec 019/021).
 *
 * The app routes by real browser paths (wouter useBrowserLocation). Identity
 * providers and Supabase email links land on `/auth/<x>` with either `?code=`
 * / `?error=` in the query string (PKCE) or an implicit payload in the URL
 * fragment (`#access_token=…`, or `#error=…&error_code=otp_expired…` for
 * expired links). Both survive path routing as-is; handlers read the combined
 * query + fragment via `takeAuthCallbackParams()`, which also scrubs consumed
 * tokens from the address bar.
 *
 * `redirectLegacyHashRoutes()` keeps pre-migration `#/...` URLs (bookmarks,
 * stale emails, old server redirects) working by rewriting them onto the
 * equivalent path before React mounts.
 */

import { STORAGE_KEYS, API_ENDPOINTS } from '@/hooks/auth-constants';

/** Supabase implicit-flow payloads: session tokens or error descriptors. */
function isAuthFragment(fragment: string): boolean {
  return /(?:^|&)(access_token|refresh_token|error|error_code|error_description)=/.test(fragment);
}

/**
 * Run before React renders (see src/main.tsx). The app used to be
 * hash-routed (`/#/projects/...`, `/?error=…#/auth/error`); rewrite any such
 * legacy URL onto the real path it names, merging a hash-route query (wouter's
 * hash navigate stored it before the `#`) with the existing search.
 */
export function redirectLegacyHashRoutes(): void {
  const { hash, search } = window.location;
  if (!hash.startsWith('#/')) return;

  const [route, hashQuery] = hash.slice(1).split('?');
  const merged = new URLSearchParams(search);
  if (hashQuery) {
    for (const [key, value] of new URLSearchParams(hashQuery)) {
      if (!merged.has(key)) merged.set(key, value);
    }
  }
  const query = merged.toString();
  window.history.replaceState(null, '', `${route}${query ? `?${query}` : ''}`);
}

/**
 * The callback params for the current landing: the query string merged with
 * the implicit URL fragment (query wins on key collisions). A consumed token
 * fragment is scrubbed from the address bar so it can't be re-read, copied,
 * or sent anywhere on refresh.
 */
export function takeAuthCallbackParams(): URLSearchParams {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (fragment && isAuthFragment(fragment)) {
    for (const [key, value] of new URLSearchParams(fragment)) {
      if (!params.has(key)) params.set(key, value);
    }
    window.history.replaceState(null, '', `${pathname}${search}`);
  }
  return params;
}

export interface ImplicitSessionTokens {
  access_token: string;
  refresh_token?: string | null;
  provider_token?: string | null;
}

/**
 * Exchange an implicit-flow Supabase session for the httpOnly sb-* cookie
 * session (ANA-16) via POST /auth/supabase-callback. Returns the
 * server-confirmed user; throws with a user-facing message on failure.
 */
export async function establishCookieSession(tokens: ImplicitSessionTokens): Promise<any> {
  const response = await fetch(API_ENDPOINTS.SUPABASE_CALLBACK, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token ?? null,
      provider_token: tokens.provider_token ?? null,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.user) {
    throw new Error(data?.message || 'Authentication failed');
  }
  return data.user;
}

/**
 * Adopt the server-confirmed identity into local state and notify the auth
 * context (this tab and others). On an account switch, drop the previous
 * account's stale tokens first. (Shared by AuthSuccess and the magic-link /
 * recovery landings — server truth, never a URL payload; ANA-20/ANA-29.)
 */
export function adoptServerIdentity(serverUser: { id?: number | string } & Record<string, unknown>): void {
  try {
    const cachedRaw = localStorage.getItem(STORAGE_KEYS.USER);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (cached?.id != null && serverUser?.id != null && cached.id !== serverUser.id) {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }
    }
  } catch {
    // ignore malformed cached user
  }

  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(serverUser));
  localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: STORAGE_KEYS.USER,
      newValue: JSON.stringify(serverUser),
    }),
  );
}
